import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	AiOutlineCheckCircle,
	AiOutlineClose,
	AiOutlineCloseCircle,
	AiOutlineExclamationCircle,
	AiOutlineInfoCircle
} from 'react-icons/ai';

import * as S from './styles';
import { returnColor } from './colorsFunction';
import SystemNotifyContext, {
	SystemNotifyItem,
	SystemNotifyPosition
} from 'context/SystemNotifyContext';
import { UseTranslationProps } from 'types/UseTranslationProps';

const closeAnimationTime = 350;

function getIcon(type: string) {
	switch (type) {
		case 'success':
			return <AiOutlineCheckCircle />;
		case 'error':
			return <AiOutlineCloseCircle />;
		case 'warning':
			return <AiOutlineExclamationCircle />;
		case 'info':
		default:
			return <AiOutlineInfoCircle />;
	}
}

interface ToastItemProps {
	item: SystemNotifyItem;
	// eslint-disable-next-line no-unused-vars
	onRemove: (id: string) => void;
	// eslint-disable-next-line no-unused-vars
	t: (value: string) => string;
}

function ToastItem({ item, onRemove, t }: ToastItemProps) {
	const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const removeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [isOpen, setIsOpen] = useState(true);

	function closeToast() {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
		}

		if (removeTimeoutRef.current) {
			clearTimeout(removeTimeoutRef.current);
		}

		setIsOpen(false);
		removeTimeoutRef.current = setTimeout(() => {
			onRemove(item.id);
		}, closeAnimationTime);
	}

	useEffect(() => {
		closeTimeoutRef.current = setTimeout(() => {
			closeToast();
		}, item.duration);

		return () => {
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
			}

			if (removeTimeoutRef.current) {
				clearTimeout(removeTimeoutRef.current);
			}
		};
		 
	}, [item.id]);

	return (
		<S.toast
			$isOpen={isOpen}
			$position={item.position}
			$typeNotify={returnColor(item.type)}
			role="status"
		>
			<S.icon $typeNotify={returnColor(item.type)}>{getIcon(item.type)}</S.icon>
			<S.message>{t(item.message) || 'Passe uma mensagem'}</S.message>
			<S.closeButton
				type="button"
				aria-label="Fechar notificacao"
				onClick={closeToast}
			>
				<AiOutlineClose />
			</S.closeButton>
			<S.progress
				$isOpen={isOpen}
				$typeNotify={returnColor(item.type)}
				$duration={item.duration}
			/>
		</S.toast>
	);
}

export default function SystemNotify() {
	const { t }: UseTranslationProps = useTranslation();
	const { notifications = [], removeNotify = () => {} } =
		useContext(SystemNotifyContext);

	if (notifications.length === 0) {
		return null;
	}

	const positions: SystemNotifyPosition[] = ['left', 'center', 'right'];

	return (
		<>
			{positions.map((position) => {
				const list = notifications.filter((item) => item.position === position);

				if (list.length === 0) {
					return null;
				}

				return (
					<S.container key={position} $position={position}>
						<S.toastList>
							{list.map((item) => (
								<ToastItem
									key={item.id}
									item={item}
									onRemove={removeNotify}
									t={t}
								/>
							))}
						</S.toastList>
					</S.container>
				);
			})}
		</>
	);
}
